import React, { useContext, useState } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { Avatar } from "./Avatar";
import { ActionTypeEnum, ChasableDetailsModel, MentionModel } from "@attelas/shared";
import Grid from "@mui/material/Grid2";
import { Comment } from "../Comment/Comment";
import { UnreadDot } from "./UnreadDot";
import { formatActivityDate } from "../../utils/formatDate";
import { openEmailByRestId } from "../../taskpane";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material";
import { ToastContext } from "../../context/ToastContext";
import { ChasablesContext, ChasableSignalRContext as SignalRContext } from "../../context/ChasablesContext";
import { useOutlook } from "../../hooks/useOutlook";

export function Mention(props: MentionModel & { onClick(): void }) {
  const theme = useTheme();
  const { isInReadMode } = useOutlook();
  const { showToast } = useContext(ToastContext);
  const isDarkMode = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { isLoading: isConnecting } = useContext(ChasablesContext);
  const [isLoading, setIsLoading] = useState(false);
  const { id, type, commentId, externalId, mentionedBy, content, createdAt, onClick } = props;

  const gotoInvoice = async () => {
    if (isConnecting) {
      showToast("Still connecting to server, please wait a moment.", "info");
      return;
    }
    setIsLoading(true);
    try {
      const data = await SignalRContext.connection?.invoke<ChasableDetailsModel>(
        "GetExternalChasable",
        externalId,
        null
      );
      if (!data) {
        showToast("Failed to get the invoice, please try again later", "error");
        return;
      }
      navigate(`/receivables/${data.customerDetails.id}/${data.invoiceDetails.id}?commentId=${commentId}`);
      onClick();
    } catch (e) {
      console.error(e);
      showToast("An error occurred while fetching the invoice.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const onClickMention = async () => {
    if (
      type === ActionTypeEnum.Chasable ||
      type === ActionTypeEnum.NewInvoice ||
      type === ActionTypeEnum.PromiseToPayBroken
    ) {
      gotoInvoice();
      return;
    }
    openEmailByRestId(externalId);
    navigate(`/activities/${externalId}?commentId=${commentId}`);
    onClick();
  };

  return (
    <Grid
      container={true}
      alignItems="center"
      flexWrap="nowrap"
      bgcolor={isDarkMode ? "common.black" : "common.white"}
      borderRadius={2}
      padding={1}
      gap={1}
      sx={{
        cursor: isLoading ? "default" : "pointer",
        opacity: isLoading ? 0.6 : 1,
        position: "relative",
      }}
      onClick={isLoading ? undefined : onClickMention}
    >
      <Grid flex="0 0 auto">
        <Avatar name={mentionedBy.fullName} />
      </Grid>
      <Grid flexGrow={1}>
        <Box>
          <Typography fontSize={12} color="text.secondary">
            {formatActivityDate(createdAt)}
          </Typography>
        </Box>
        <Typography
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          <Comment content={content} renderMentionAsNormal={true} />
        </Typography>
      </Grid>
      <Grid flex="0 0 auto">
        {isLoading ? <CircularProgress size={20} /> : <UnreadDot mentionId={id} mentionType={type} />}
      </Grid>
    </Grid>
  );
}
